import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../components/layout/Header'
import { Card } from '../components/common/Card'
import { Button } from '../components/common/Button'
import axios from 'axios'
import { useTheme } from '../contexts/ThemeContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

interface MarketDetails {
  country: string
  country_code: string
  region: string
  market_overview: {
    population: number
    gdp: number
    gdp_growth: number
    currency: string
    language: string
    timezone: string
  }
  ecommerce_stats: {
    market_size: number
    growth_rate: number
    online_penetration: number
    avg_order_value: number
  }
  expandly_presence: {
    seller_count: number
    total_orders: number
    total_revenue: number
    avg_order_value: number
    top_categories: { name: string; percentage: number }[]
    top_sellers: { name: string; revenue: number }[]
  }
  trade_data: {
    exports: number
    imports: number
    trade_balance: number
    top_partners: string[]
  }
  entry_requirements: {
    vat_registration: boolean
    local_entity: boolean
    product_compliance: string[]
    customs_duties: string
    shipping_considerations: string
  }
  opportunities: string[]
  challenges: string[]
}

export const MarketDetailsPage: React.FC = () => {
  const { country } = useParams<{ country: string }>()
  const { colors } = useTheme()
  const navigate = useNavigate()
  const [showAiAnalysis, setShowAiAnalysis] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['market-details', country],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/markets/${country}`)
      return data as MarketDetails
    },
    enabled: !!country,
  })

  const { data: aiAnalysis, isLoading: aiLoading } = useQuery({
    queryKey: ['market-ai-analysis', country],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/markets/ai-analysis/${country}`)
      return data
    },
    enabled: !!country && showAiAnalysis,
  })

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E0E0E0',
              borderTop: '4px solid #4A90E2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: colors.textSecondary }}>Loading market details...</p>
          </div>
        </main>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <Card style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#EF4444', marginBottom: '16px' }}>
              Error loading market details: {(error as Error)?.message || 'Market not found'}
            </p>
            <Button onClick={() => navigate('/markets')}>Back to Markets</Button>
          </Card>
        </main>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/markets')}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textSecondary,
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '12px',
              padding: 0,
            }}
          >
            ← Back to Markets
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
                {data.country}
              </h1>
              <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
                {data.region} | {data.market_overview?.currency || 'N/A'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button variant="outline" onClick={() => navigate(`/compliance/${country}`)}>
                View Compliance
              </Button>
              <Button onClick={() => setShowAiAnalysis(true)}>
                AI Market Analysis
              </Button>
            </div>
          </div>
        </div>

        {/* Market Overview */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '24px' }}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Population</p>
              <p style={{ fontSize: '28px', fontWeight: 700, color: colors.primary }}>
                {((data.market_overview?.population || 0) / 1000000).toFixed(1)}M
              </p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>GDP</p>
              <p style={{ fontSize: '28px', fontWeight: 700, color: '#50C878' }}>
                ${((data.market_overview?.gdp || 0) / 1000000000000).toFixed(2)}T
              </p>
              <p style={{ fontSize: '12px', color: data.market_overview?.gdp_growth >= 0 ? '#50C878' : '#EF4444' }}>
                {data.market_overview?.gdp_growth >= 0 ? '+' : ''}{data.market_overview?.gdp_growth || 0}% growth
              </p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>E-commerce Market</p>
              <p style={{ fontSize: '28px', fontWeight: 700, color: '#7B68EE' }}>
                ${((data.ecommerce_stats?.market_size || 0) / 1000000000).toFixed(1)}B
              </p>
              <p style={{ fontSize: '12px', color: '#50C878' }}>
                +{data.ecommerce_stats?.growth_rate || 0}% growth
              </p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Expandly Sellers</p>
              <p style={{ fontSize: '28px', fontWeight: 700, color: '#FF6B35' }}>
                {data.expandly_presence?.seller_count || 0}
              </p>
              <p style={{ fontSize: '12px', color: colors.textSecondary }}>
                ${(data.expandly_presence?.total_revenue || 0).toLocaleString()} revenue
              </p>
            </div>
          </Card>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', marginBottom: '24px' }}>
          {/* Expandly Performance */}
          <Card>
            <h2 style={{ fontSize: '20px', marginBottom: '16px', color: colors.textPrimary }}>Expandly Performance in {data.country}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '20px' }}>
              <div>
                <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '4px' }}>Total Orders</p>
                <p style={{ fontSize: '24px', fontWeight: 600, color: colors.textPrimary }}>
                  {(data.expandly_presence?.total_orders || 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '4px' }}>Avg Order Value</p>
                <p style={{ fontSize: '24px', fontWeight: 600, color: colors.textPrimary }}>
                  ${(data.expandly_presence?.avg_order_value || 0).toFixed(2)}
                </p>
              </div>
            </div>

            <h3 style={{ fontSize: '16px', marginBottom: '12px', color: colors.textPrimary }}>Top Categories</h3>
            {(data.expandly_presence?.top_categories || []).map((cat, idx) => (
              <div key={cat.name} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 0',
                borderBottom: idx < (data.expandly_presence?.top_categories?.length || 0) - 1 ? `1px solid ${colors.border}` : 'none',
              }}>
                <span style={{ color: colors.textPrimary }}>{cat.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '100px',
                    height: '8px',
                    backgroundColor: colors.border,
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${cat.percentage}%`,
                      height: '100%',
                      backgroundColor: colors.primary,
                      borderRadius: '4px',
                    }} />
                  </div>
                  <span style={{ fontWeight: 500, color: colors.textPrimary, minWidth: '40px' }}>{cat.percentage}%</span>
                </div>
              </div>
            ))}
          </Card>

          {/* Entry Requirements */}
          <Card>
            <h2 style={{ fontSize: '20px', marginBottom: '16px', color: colors.textPrimary }}>Entry Requirements</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: data.entry_requirements?.vat_registration ? '#EF444420' : '#50C87820',
                  color: data.entry_requirements?.vat_registration ? '#EF4444' : '#50C878',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                }}>
                  {data.entry_requirements?.vat_registration ? '!' : '✓'}
                </span>
                <span style={{ color: colors.textPrimary }}>VAT Registration {data.entry_requirements?.vat_registration ? 'Required' : 'Not Required'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: data.entry_requirements?.local_entity ? '#EF444420' : '#50C87820',
                  color: data.entry_requirements?.local_entity ? '#EF4444' : '#50C878',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                }}>
                  {data.entry_requirements?.local_entity ? '!' : '✓'}
                </span>
                <span style={{ color: colors.textPrimary }}>Local Entity {data.entry_requirements?.local_entity ? 'Required' : 'Not Required'}</span>
              </div>
            </div>

            {data.entry_requirements?.product_compliance?.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Product Compliance</p>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {data.entry_requirements.product_compliance.map(item => (
                    <span key={item} style={{
                      padding: '4px 8px',
                      backgroundColor: '#F59E0B20',
                      color: '#F59E0B',
                      borderRadius: '4px',
                      fontSize: '12px',
                    }}>{item}</span>
                  ))}
                </div>
              </div>
            )}

            {data.entry_requirements?.customs_duties && (
              <div style={{ marginTop: '16px' }}>
                <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '4px' }}>Customs & Duties</p>
                <p style={{ color: colors.textPrimary, fontSize: '14px' }}>{data.entry_requirements.customs_duties}</p>
              </div>
            )}

            <Button
              variant="outline"
              style={{ marginTop: '16px', width: '100%' }}
              onClick={() => navigate(`/compliance/${country}`)}
            >
              Full Compliance Details →
            </Button>
          </Card>
        </div>

        {/* Opportunities & Challenges */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          <Card style={{ borderLeft: '4px solid #50C878' }}>
            <h2 style={{ fontSize: '18px', marginBottom: '16px', color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📈</span> Opportunities
            </h2>
            <ul style={{ margin: 0, paddingLeft: '20px', color: colors.textSecondary, lineHeight: 1.8 }}>
              {(data.opportunities || []).map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </Card>
          <Card style={{ borderLeft: '4px solid #EF4444' }}>
            <h2 style={{ fontSize: '18px', marginBottom: '16px', color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>⚠️</span> Challenges
            </h2>
            <ul style={{ margin: 0, paddingLeft: '20px', color: colors.textSecondary, lineHeight: 1.8 }}>
              {(data.challenges || []).map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </Card>
        </div>

        {/* AI Analysis Section */}
        {showAiAnalysis && (
          <Card style={{
            background: `linear-gradient(135deg, ${colors.cardBackground}, #7B68EE10)`,
            border: '1px solid #7B68EE40',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <span style={{ fontSize: '24px' }}>🤖</span>
              <h2 style={{ fontSize: '20px', color: colors.textPrimary }}>AI Market Analysis: {data.country}</h2>
            </div>

            {aiLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #E0E0E0',
                  borderTop: '4px solid #7B68EE',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px',
                }} />
                <p style={{ color: colors.textSecondary }}>Generating AI analysis...</p>
              </div>
            ) : (
              <div style={{
                color: colors.textSecondary,
                lineHeight: 1.8,
                whiteSpace: 'pre-wrap',
              }}>
                {aiAnalysis?.analysis || 'Analysis not available'}
              </div>
            )}
          </Card>
        )}
      </main>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
